import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Brain, AlertTriangle, TrendingDown, Activity, Users,
  HeartPulse, Hospital, Clock, ChevronRight, Search,
  AlertCircle, CheckCircle, BarChart3, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PredictiveAIDashboard = () => {
  const [activeTab, setActiveTab] = useState('population');
  const [populationData, setPopulationData] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [aiModels, setAIModels] = useState(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientPrediction, setPatientPrediction] = useState(null);
  const [hospitalRisk, setHospitalRisk] = useState(null);
  const [careIntensity, setCareIntensity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [popRes, alertsRes, modelsRes] = await Promise.all([
        fetch(`${API_URL}/api/phase33/ai/population/dashboard`),
        fetch(`${API_URL}/api/phase33/ai/population/alerts`),
        fetch(`${API_URL}/api/phase33/ai/models`)
      ]);

      if (popRes.ok) setPopulationData(await popRes.json());
      if (alertsRes.ok) setAlerts(await alertsRes.json());
      if (modelsRes.ok) setAIModels(await modelsRes.json());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    }
    setLoading(false);
  };

  const fetchPatientPredictions = async (patientId) => {
    setSelectedPatient(patientId);
    try {
      const [declineRes, hospRes, intensityRes] = await Promise.all([
        fetch(`${API_URL}/api/phase33/ai/decline/${patientId}`),
        fetch(`${API_URL}/api/phase33/ai/hospitalization-risk/${patientId}`),
        fetch(`${API_URL}/api/phase33/ai/care-intensity/${patientId}`)
      ]);

      if (declineRes.ok) setPatientPrediction(await declineRes.json());
      if (hospRes.ok) setHospitalRisk(await hospRes.json());
      if (intensityRes.ok) setCareIntensity(await intensityRes.json());
    } catch (error) {
      console.error('Error fetching patient predictions:', error);
      toast.error('Failed to load patient predictions');
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'moderate': return 'text-amber-600 bg-amber-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      default: return 'bg-blue-500 text-white';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="predictive-ai-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Brain className="h-7 w-7 text-purple-600" />
            Predictive AI Dashboard
          </h1>
          <p className="text-gray-600 mt-1">AI-powered patient decline and hospitalization risk predictions</p>
        </div>
        <Button onClick={fetchDashboardData} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-4 w-full max-w-xl">
          <TabsTrigger value="population" data-testid="tab-population">Population</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">Alerts</TabsTrigger>
          <TabsTrigger value="patient" data-testid="tab-patient">Patient Lookup</TabsTrigger>
          <TabsTrigger value="models" data-testid="tab-models">AI Models</TabsTrigger>
        </TabsList>

        {/* Population Tab */}
        <TabsContent value="population" className="space-y-6">
          {/* Risk Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-700">High Risk</p>
                    <p className="text-3xl font-bold text-red-600">
                      {populationData?.high_risk?.count || 0}
                    </p>
                  </div>
                  <AlertTriangle className="h-10 w-10 text-red-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-700">Moderate Risk</p>
                    <p className="text-3xl font-bold text-amber-600">
                      {populationData?.moderate_risk?.count || 0}
                    </p>
                  </div>
                  <Activity className="h-10 w-10 text-amber-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700">Low Risk</p>
                    <p className="text-3xl font-bold text-green-600">
                      {populationData?.low_risk?.count || 0}
                    </p>
                  </div>
                  <CheckCircle className="h-10 w-10 text-green-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Patients</p>
                    <p className="text-3xl font-bold text-gray-700">
                      {populationData?.total_patients || 0}
                    </p>
                  </div>
                  <Users className="h-10 w-10 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* High Risk Patients List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                High Risk Patients - Immediate Attention Required
              </CardTitle>
            </CardHeader>
            <CardContent>
              {populationData?.high_risk?.patients?.length > 0 ? (
                <div className="space-y-3">
                  {populationData.high_risk.patients.map((patient) => (
                    <div
                      key={patient.patient_id}
                      className="flex items-center justify-between p-4 border border-red-200 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100"
                      onClick={() => {
                        fetchPatientPredictions(patient.patient_id);
                        setActiveTab('patient');
                      }}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-red-200 rounded-full">
                          <HeartPulse className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium">{patient.name}</p>
                          <p className="text-sm text-gray-600">ID: {patient.patient_id}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Risk Score</p>
                          <p className="text-xl font-bold text-red-600">{patient.risk_score}%</p>
                        </div>
                        {patient.alerts_count > 0 && (
                          <Badge className="bg-red-500">{patient.alerts_count} alerts</Badge>
                        )}
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-400" />
                  <p>No high-risk patients currently</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Moderate Risk Patients */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <Activity className="h-5 w-5" />
                Moderate Risk Patients - Monitor Closely
              </CardTitle>
            </CardHeader>
            <CardContent>
              {populationData?.moderate_risk?.patients?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {populationData.moderate_risk.patients.slice(0, 6).map((patient) => (
                    <div
                      key={patient.patient_id}
                      className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                      onClick={() => {
                        fetchPatientPredictions(patient.patient_id);
                        setActiveTab('patient');
                      }}
                    >
                      <div>
                        <p className="font-medium">{patient.name}</p>
                        <p className="text-sm text-gray-500">Score: {patient.risk_score}%</p>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700">Moderate</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No moderate-risk patients</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="border-red-200">
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-red-600">{alerts?.critical_count || 0}</p>
                <p className="text-sm text-gray-500">Critical Alerts</p>
              </CardContent>
            </Card>
            <Card className="border-orange-200">
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-orange-600">{alerts?.high_count || 0}</p>
                <p className="text-sm text-gray-500">High Priority</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-gray-700">{alerts?.total_alerts || 0}</p>
                <p className="text-sm text-gray-500">Total Alerts</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Active Alerts</CardTitle>
            </CardHeader>
            <CardContent>
              {alerts?.alerts?.length > 0 ? (
                <div className="space-y-3">
                  {alerts.alerts.map((alert, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-4 p-4 border rounded-lg"
                    >
                      <Badge className={getSeverityColor(alert.severity)}>
                        {alert.severity?.toUpperCase()}
                      </Badge>
                      <div className="flex-1">
                        <p className="font-medium">{alert.patient_name}</p>
                        <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                        {alert.recommended_actions?.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-gray-500">Recommended Actions:</p>
                            <ul className="list-disc list-inside text-sm text-gray-600">
                              {alert.recommended_actions.slice(0, 3).map((action, i) => (
                                <li key={i}>{action}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-400" />
                  <p>No active alerts</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Patient Lookup Tab */}
        <TabsContent value="patient" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Patient Risk Analysis</CardTitle>
              <CardDescription>Enter a patient ID to view detailed predictions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Enter Patient ID..."
                    className="pl-10"
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    data-testid="patient-search"
                  />
                </div>
                <Button 
                  onClick={() => fetchPatientPredictions(patientSearch)}
                  disabled={!patientSearch}
                >
                  Analyze
                </Button>
              </div>
            </CardContent>
          </Card>

          {selectedPatient && patientPrediction && (
            <>
              {/* Decline Prediction */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="h-5 w-5 text-purple-600" />
                    Decline Prediction - {selectedPatient}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {['7_day', '30_day', '90_day'].map((period) => {
                      const pred = patientPrediction.predictions?.[period];
                      return (
                        <div key={period} className="text-center p-4 border rounded-lg">
                          <p className="text-sm text-gray-500 mb-2">
                            {period.replace('_', ' ').replace('day', 'Day')} Risk
                          </p>
                          <p className="text-3xl font-bold mb-1">{pred?.risk_score || 0}%</p>
                          <Badge className={getRiskColor(pred?.risk_level)}>
                            {pred?.risk_level?.toUpperCase() || 'N/A'}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>

                  {patientPrediction.contributing_factors?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3">Contributing Factors</h4>
                      <div className="space-y-2">
                        {patientPrediction.contributing_factors.map((factor, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span>{factor.factor}</span>
                            <Badge variant="outline" className={getRiskColor(factor.impact)}>
                              {factor.impact}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {patientPrediction.alerts?.length > 0 && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h4 className="font-medium text-red-700 mb-2">Active Alerts</h4>
                      {patientPrediction.alerts.map((alert, idx) => (
                        <div key={idx} className="mb-2">
                          <p className="text-sm font-medium text-red-600">{alert.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Hospitalization Risk */}
              {hospitalRisk && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Hospital className="h-5 w-5 text-blue-600" />
                      Hospitalization Risk
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-6 mb-6">
                      <div className="text-center">
                        <p className="text-4xl font-bold">{hospitalRisk.risk_score}%</p>
                        <Badge className={`mt-2 ${getRiskColor(hospitalRisk.risk_level)}`}>
                          {hospitalRisk.risk_level?.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex-1">
                        <Progress value={hospitalRisk.risk_score} className="h-4" />
                      </div>
                    </div>

                    {hospitalRisk.recommended_interventions?.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Recommended Interventions</h4>
                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                          {hospitalRisk.recommended_interventions.map((intervention, idx) => (
                            <li key={idx}>{intervention}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Care Intensity */}
              {careIntensity && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-teal-600" />
                      Care Intensity Forecast
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      {careIntensity.forecasts?.map((forecast, idx) => (
                        <div key={idx} className="p-4 border rounded-lg">
                          <p className="text-sm text-gray-500 capitalize">
                            {forecast.period?.replace(/_/g, ' ')}
                          </p>
                          <p className="text-2xl font-bold mt-1">
                            {forecast.predicted_visits || forecast.predicted_visits_per_week || 0} visits
                          </p>
                          <Badge variant="outline" className="mt-2 capitalize">
                            {forecast.care_level} care
                          </Badge>
                        </div>
                      ))}
                    </div>

                    {careIntensity.staffing_recommendations && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium mb-2">Staffing Recommendations</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Nursing Hours:</span>
                            <span className="ml-2 font-medium">
                              {careIntensity.staffing_recommendations.nursing_hours} hrs/week
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Aide Hours:</span>
                            <span className="ml-2 font-medium">
                              {careIntensity.staffing_recommendations.aide_hours} hrs/week
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* AI Models Tab */}
        <TabsContent value="models" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-purple-600" />
                Available Predictive Models
              </CardTitle>
              <CardDescription>AI models powering the predictive analytics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {aiModels?.models?.map((model) => (
                  <div key={model.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">{model.name}</h4>
                      <Badge variant="outline">v{model.version}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{model.description}</p>
                    <div className="flex items-center gap-6 text-sm">
                      <div>
                        <span className="text-gray-500">Accuracy:</span>
                        <span className="ml-2 font-medium text-green-600">
                          {(model.accuracy * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Last Trained:</span>
                        <span className="ml-2">{model.last_trained}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PredictiveAIDashboard;
